<template>
  <div>
    <h2 class="pt-2 mb-4">{{ heading }}</h2>
    <MobialisFormWithValidation
      @onValidate="submit"
      novalidate
      v-slot="{ errors }"
      v-av-analytics-form-submit="{ elementText: btnText }"
    >
      <div class="row">
        <div class="col-sm-6">
          <mobialis-text-input-with-validation
            :rules="{ length: 17 }"
            :label="$t('message.trade.additionalVehicleDetails.vin')"
            v-model="vehicleVin"
            vid="vin"
            data-cy="vin"
          >
            <template v-slot:icon>
              <a data-toggle="modal" data-target="#tooltip" role="button">
                <img
                  class="ml-2 mb-1"
                  src="@/assets/question-circle.svg"
                  :alt="$t('message.trade.additionalVehicleDetails.vinTooltipHeading')"
                />
              </a>
            </template>
          </mobialis-text-input-with-validation>
          <tool-tip
            :title="$t('message.trade.additionalVehicleDetails.vinTooltipHeading')"
            :body="$t('message.trade.additionalVehicleDetails.vinTooltipContent')"
          >
          </tool-tip>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6">
          <mobialis-text-input-with-validation
            :label="$t('message.trade.additionalVehicleDetails.colour')"
            v-model="vehicleColour"
            vid="colour"
            data-cy="colour"
          ></mobialis-text-input-with-validation>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6">
          <mobialis-selection-input-with-validation
            rules="required"
            :label="$t('message.trade.additionalVehicleDetails.condition')"
            v-model="vehicleCondition"
            :placeHolder="$t('message.common.select')"
            vid="vehicleCondition"
            :options="vehicleConditionOptions"
            data-cy="vehicleCondition"
          ></mobialis-selection-input-with-validation>
        </div>
      </div>
      <h3 class="pt-2 mb-4">{{ $t('message.trade.additionalVehicleDetails.heading2') }}</h3>
      <div class="row">
        <div class="col-sm-10">
          <div class="label">{{ $t('message.trade.additionalVehicleDetails.accidentLabel') }}</div>
          <div class="row form-row mb-3">
            <MobialisFancyRadio
              class="radio-btn-container"
              vid="vehicleBeenInAccident"
              rules="required"
              :textArray="yesNoOptions"
              name="vehicleBeenInAccident"
              v-model="vehicleBeenInAccident"
            ></MobialisFancyRadio>
            <div class="col">
              <p
                class="text-danger small my-0"
                v-if="errors.vehicleBeenInAccident && errors.vehicleBeenInAccident.length > 0"
              >
                {{ $t('message.common.pleaseSelect') }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-sm-10">
          <div class="label">{{ $t('message.trade.additionalVehicleDetails.bodyWorkLabel') }}</div>
          <div class="row form-row mb-3">
            <MobialisFancyRadio
              class="radio-btn-container"
              vid="vehicleNeedsBodyWork"
              rules="required"
              :textArray="yesNoOptions"
              name="vehicleNeedsBodyWork"
              v-model="vehicleNeedsBodyWork"
            ></MobialisFancyRadio>
            <div class="col">
              <p
                class="text-danger small my-0"
                v-if="errors.vehicleNeedsBodyWork && errors.vehicleNeedsBodyWork.length > 0"
              >
                {{ $t('message.common.pleaseSelect') }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-sm-10">
          <div class="label">{{ $t('message.trade.additionalVehicleDetails.mechanicalIssuesLabel') }}</div>
          <div class="row form-row mb-3">
            <MobialisFancyRadio
              class="radio-btn-container"
              vid="vehicleHasMechanicalIssues"
              rules="required"
              :textArray="yesNoOptions"
              name="vehicleHasMechanicalIssues"
              v-model="vehicleHasMechanicalIssues"
            ></MobialisFancyRadio>
            <div class="col">
              <p
                class="text-danger small my-0"
                v-if="errors.vehicleHasMechanicalIssues && errors.vehicleHasMechanicalIssues.length > 0"
              >
                {{ $t('message.common.pleaseSelect') }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-sm-7">
          <mobialis-textarea
            rows="3"
            maxlength="255"
            vid="additionalDetails"
            v-model="vehicleAdditionalDetails"
            data-cy="additionalDetails"
            :label="$t('message.trade.additionalVehicleDetails.additionalDetails')"
          />
        </div>
      </div>
      <mobialis-button type="submit" class="mt-4 mb-1" data-cy="continue">{{ btnText }} </mobialis-button>
      <p class="text-center small my-0">{{ disclaimer }}</p>
    </MobialisFormWithValidation>
  </div>
</template>

<script>
import cloneDeep from 'lodash/fp/cloneDeep';
import { mapGetters } from 'vuex';
import { AVRProducts, YesNo, VehicleConditionOptions } from '../../utils/enums';
import ToolTip from '@/components/ToolTip.vue';

export default {
  name: 'AdditionalVehicleDetails',
  components: {
    ToolTip,
  },
  data() {
    return {
      vehicleVin: cloneDeep(this.$store.state.trade.vehicle.vin),
      vehicleColour: cloneDeep(this.$store.state.trade.vehicle.additionalVehicleAttributes.colour),
      vehicleCondition: cloneDeep(this.$store.state.trade.vehicle.additionalVehicleAttributes.condition),
      vehicleBeenInAccident: cloneDeep(this.$store.state.trade.vehicle.additionalVehicleAttributes.beenInAccident),
      vehicleNeedsBodyWork: cloneDeep(this.$store.state.trade.vehicle.additionalVehicleAttributes.needsBodyWork),
      vehicleHasMechanicalIssues: cloneDeep(
        this.$store.state.trade.vehicle.additionalVehicleAttributes.hasMechanicalIssues
      ),
      vehicleAdditionalDetails: cloneDeep(this.$store.state.trade.vehicle.additionalDetails),
      yesNoOptions: [
        {
          value: YesNo.YES,
          label: this.$t('message.common.yes'),
        },
        {
          value: YesNo.NO,
          label: this.$t('message.common.no'),
        },
      ],
      vehicleConditionOptions: [
        {
          value: VehicleConditionOptions.EXCELLENT,
          label: this.$t('message.trade.additionalVehicleDetails.excellent'),
        },
        {
          value: VehicleConditionOptions.GOOD,
          label: this.$t('message.trade.additionalVehicleDetails.good'),
        },
        {
          value: VehicleConditionOptions.FAIR,
          label: this.$t('message.trade.additionalVehicleDetails.fair'),
        },
        {
          value: VehicleConditionOptions.POOR,
          label: this.$t('message.trade.additionalVehicleDetails.poor'),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      mobileVerificationEnabled: 'business/mobileVerificationEnabled',
      getCurrentProduct: 'getCurrentProduct',
    }),
    isAcquire() {
      return this.getCurrentProduct === AVRProducts.ACQUIRE;
    },
    isTrade() {
      return this.getCurrentProduct === AVRProducts.TRADE;
    },
    heading() {
      return this.isAcquire
        ? this.$t('message.trade.additionalVehicleDetails.acquireHeading')
        : this.$t('message.trade.additionalVehicleDetails.heading');
    },
    btnText() {
      return this.isAcquire
        ? this.$t('message.trade.additionalVehicleDetails.acquireBtn')
        : this.$t('message.trade.additionalVehicleDetails.btn');
    },
    disclaimer() {
      return this.isAcquire
        ? this.$t('message.trade.additionalVehicleDetails.acquireDisclaimer')
        : this.$t('message.trade.additionalVehicleDetails.disclaimer');
    },
  },
  methods: {
    async submit(isValid) {
      if (!isValid) {
        return;
      }
      this.$store.commit('trade/SET_VEHICLE_ADDITIONAL_DETAILS', this.vehicleAdditionalDetails);
      this.$store.commit('trade/SET_VEHICLE_VIN', this.vehicleVin);
      this.$store.commit('trade/SET_VEHICLE_ADDITIONAL_VEHICLE_ATTRIBUTES', {
        colour: this.vehicleColour,
        condition: this.vehicleCondition,
        beenInAccident: this.vehicleBeenInAccident,
        needsBodyWork: this.vehicleNeedsBodyWork,
        hasMechanicalIssues: this.vehicleHasMechanicalIssues,
      });
      if (this.isAcquire) {
        this.$store.commit('trade/SET_LEAD_ACQUIRE_VEHICLE');
      } else {
        this.$store.commit('trade/SET_LEAD_TRADEIN_VEHICLE');
      }
      try {
        await this.$store.dispatch('lead/updateLead');
        if (this.mobileVerificationEnabled) {
          this.$router.push({ path: `${this.getProductBasePath()}/mobile-verification` });
        } else {
          this.$router.push({ path: `${this.getProductBasePath()}/report` });
        }
      } catch (err) {
        this.$log(`Error in ${this.getCurrentProduct} additional info form - Update Lead`, err);
        this.$router.push({ path: `${this.getProductBasePath()}/error` });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.label {
  margin: 1.5px 0 2px;
  height: auto;
  min-height: 28px;
  line-height: 1.75;
  display: block;
  color: #19222b;
  font-weight: 500;
}

a {
  cursor: pointer;
}

.radio-btn-container::v-deep {
  width: 100%;
  display: flex;
  gap: 10px;
  .radio-btn {
    flex: 1;
    width: 50%;
  }
  @media only screen and (max-width: $breakpoint-mobile) {
    display: block;
    .radio-btn {
      width: 100%;
    }
  }
}
</style>
